/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, useHistory, useLocation, useParams, Switch } from "react-router-dom";

import { CssBaseline, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import {
    FIDP, RETURN_TO_PATH, RESOURCE_LOGIN, RESOURCE_SIGN_IN, SELECTED_ORG_UUID,
    ORG_QUERY_PARAM, PATH_SEPARATOR
} from "../data/constants";
import { ApplicationState, NotificationReducer, StorageOperations, UserReducer } from '../data/interfaces';
import { fetchApplicationDetails, resetNotificationMsg } from '../data/operations';
import { isProductionKeyGenerationAccessible, isSandboxKeyGenerationAccessible, isSelfSignupEnabled, localStoreOperation, sessionStoreOperation } from "../data/utils";
import AppOverview from "./app/AppOverview";
import AppProductionKeys from "./app/AppProductionKeys";
import AppSubscriptions from "./app/AppSubscriptions";
import DevPortalFooter from './shared/DevPortalFooter';
import MainMenuDrawer from './shared/MainMenuDrawer/index';
import NoPermission from './shared/NoPermission';
import Settings from '../conf/settings';
import ApplicationApiSubscriptions from './app/AppSubscriptions/ApplicationApiSubscriptoins';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex'
    }
}));

interface AppProps {
    menuDrawerOpen: boolean,
    userReducer: UserReducer,
    notificationReducer: NotificationReducer,
    fetchApplicationDetails: (applicationId: string) => Promise<void>
    resetNotificationMessage: () => void;
}

function AppPage(props: AppProps) {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const {
        userReducer, notificationReducer, menuDrawerOpen, fetchApplicationDetails, resetNotificationMessage
    } = props;
    const { orgName, applicationId, subscriptionId } = useParams<{orgName: string; applicationId: string, subscriptionId: string}>();
    const { enqueueSnackbar } = useSnackbar();
    const { apimTokenObtained, orgs: userOrganizations, user } = userReducer;
    const allowedScopes = user.allowedScopes;
    const { type: notificationType, message: notification } = notificationReducer;

    const checkUserAccessToCurrentPathOrg = (): boolean => {
        const selectedOrganization = userOrganizations.find(userOrg => userOrg.handle === orgName);
        return !!selectedOrganization;
    }

    const hasUserAccessToCurrentPathOrg = checkUserAccessToCurrentPathOrg();

    useEffect(() => {
        if (notification !== "") {
            enqueueSnackbar(notification, { variant: notificationType });
            resetNotificationMessage();
        }
    }, [notificationReducer]);

    useEffect(() => {
        if (!apimTokenObtained) {
            sessionStoreOperation(StorageOperations.ADD, RETURN_TO_PATH, window.location.pathname);
            const queryParam = new URLSearchParams(location.search);
            const orgUuid = queryParam.get(ORG_QUERY_PARAM);
            if (orgUuid) {
                localStoreOperation(StorageOperations.ADD, SELECTED_ORG_UUID, orgUuid);
            }
            // Checking whether request is from choreo portal and setting fidp value
            if (queryParam.get('fidp')) {
                localStoreOperation(StorageOperations.ADD, FIDP, queryParam.get('fidp') ?? "");
                history.push(RESOURCE_SIGN_IN);
            } else if (localStoreOperation(StorageOperations.GET, FIDP)) {
                // Redirecting user to sigin if fidp value already exists in local store
                history.push(RESOURCE_SIGN_IN);
            } else if (!isSelfSignupEnabled(orgName)) {
                // Redirecting user to login page if fidp value does not exist
                // and selected org is not a self sign up enabled org
                history.push(RESOURCE_LOGIN);
            } else {
                // Redirecting user to devportal home page if fidp value does not exist
                // and selected org have self signup enabled
                history.push(`/${orgName}`);
            }
        } else if (hasUserAccessToCurrentPathOrg) {
            fetchApplicationDetails(applicationId);
        }
    }, []);

    const getCredentialGenerationPath = () => {
        const pathList = [];
        if (isProductionKeyGenerationAccessible(allowedScopes)) {
            pathList.push(
                PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/credentials/production',
                PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/credentials/production/oauth'
            );
        }
        if (Settings.sandboxKeyGenerationEnabled && isSandboxKeyGenerationAccessible(allowedScopes)) {
            pathList.push(
                PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/credentials/sandbox',
                PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/credentials/sandbox/oauth'
            );
        }
        return pathList;
    }

    return hasUserAccessToCurrentPathOrg && user.allowedScopes.includes('apim:subscribe')? (
        <div className={classes.root}>
            <CssBaseline />
            <Switch>
                <Route exact={true} path={PATH_SEPARATOR + orgName + '/applications/' + applicationId}>
                    <MainMenuDrawer menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} />
                    <Redirect to={PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/overview'} />
                </Route>
                <Route exact={true} path={PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/overview'}>
                    <MainMenuDrawer menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} />
                    <AppOverview />
                </Route>
                <Route path={getCredentialGenerationPath()} >
                    <MainMenuDrawer menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} />
                    <AppProductionKeys />
                </Route>
                <Route path={PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/subscriptions'}>
                    <MainMenuDrawer menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} />
                    <AppSubscriptions />
                </Route>
                <Route path={PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/new-subscriptions'}>
                    <MainMenuDrawer menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} />
                    <ApplicationApiSubscriptions menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} applicationId={applicationId}/>
                </Route>
                <Route path={PATH_SEPARATOR + orgName + '/applications/' + applicationId + '/edit-subscription/' + subscriptionId}>
                    <MainMenuDrawer menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} />
                    <ApplicationApiSubscriptions menuDrawerOpen={menuDrawerOpen} organizationHandle={orgName} applicationId={applicationId} subscriptionId={subscriptionId} />
                </Route>
                <DevPortalFooter />
            </Switch>
        </div>
        ):
        (
            <div className={classes.root}>
                <CssBaseline />
                <NoPermission />
            </div>
        )
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        userReducer: state.userReducer,
        notificationReducer: state.notificationReducer
    }
};

const mapDispatchToProps = {
    resetNotificationMessage: resetNotificationMsg,
    fetchApplicationDetails
};

export default connect(mapStateToProps, mapDispatchToProps)(AppPage);
